import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State } from './cash-flow.reducer';
import { CashFlowFilter, Periods } from './cash-flow.models';
import * as moment from 'moment';
import { CommonConstants, DatesInterval } from '../../global/interfaces';
import { getMonthsDropdownValues, getQuartersDropdownValues, getYearsDropdownValuesCashFlow } from '../../global/dates';


export const selectCashFlowState = createFeatureSelector<State>('cashFlow');

export const selectCashFlowLoading = createSelector(selectCashFlowState, (state: State) => state.loading);

export const selectCashFlowData = createSelector(selectCashFlowState, (state: State) => state.data);

export const selectCashFlowFilter = createSelector(selectCashFlowState, (state: State) => state.filter);

export const selectCashFlowFilterPeriod = createSelector(selectCashFlowFilter, (state: CashFlowFilter) => state.period);

export const selectCashFlowFilterYear = createSelector(selectCashFlowFilter, (state: CashFlowFilter) => state.year);

export const selectCashFlowFilterMonth = createSelector(selectCashFlowFilter, (state: CashFlowFilter) => state.month);

export const selectCashFlowFilterQuarter = createSelector(selectCashFlowFilter, (state: CashFlowFilter) => state.quarter);
export const selectCashFlowSort = createSelector(selectCashFlowState, (state: State) => state.sort);

export const selectCashFlowFrom = createSelector(
  selectCashFlowFilterPeriod,
  selectCashFlowFilterYear,
  selectCashFlowFilterMonth,
  selectCashFlowFilterQuarter,
  (period, year, month, quarter) => {
    let from;

    switch (period) {
      case Periods.MONTH:
        if (month === undefined || year === undefined) {
          return undefined;
        }

        from = moment.utc({ year, month }).startOf('month');
        break;
      case Periods.QUARTER:
        if (quarter === undefined || year === undefined) {
          return undefined;
        }

        from = moment.utc({ year }).quarter(quarter).startOf('quarter');
        break;
    }

    return from ? (from.day() % 6 === 0
      ? from.add(1, 'day').day(1).format(CommonConstants.DBDateFormat)
      : from.format(CommonConstants.DBDateFormat)) : undefined;

  }
);

export const selectCashFlowTo = createSelector(
  selectCashFlowFilterPeriod,
  selectCashFlowFilterYear,
  selectCashFlowFilterMonth,
  selectCashFlowFilterQuarter,
  (period, year, month, quarter) => {
    let to;

    switch (period) {
      case Periods.MONTH:
        if (month === undefined || year === undefined) {
          return undefined;
        }

        to = moment.utc({ year, month }).endOf('month');
        break;
      case Periods.QUARTER:
        if (quarter === undefined || year === undefined) {
          return undefined;
        }

        to = moment.utc({ year }).quarter(quarter).endOf('quarter');
        break;
    }

    return to ? (to.day() % 6 === 0
      ? to.subtract(1, 'day').day(5).format(CommonConstants.DBDateFormat)
      : to.format(CommonConstants.DBDateFormat)) : undefined;
  }
);

export const selectCashFlowDates = createSelector(selectCashFlowState, (state: State) => state.dates);

export const selectCashFlowDatesMin = createSelector(selectCashFlowDates, (dates?: DatesInterval) =>
  dates ? moment.utc(dates.min) : undefined
);

export const selectCashFlowDatesMax = createSelector(selectCashFlowDates, (dates?: DatesInterval) =>
  dates ? moment.utc(dates.max) : undefined
);

export const selectCashFlowDate = createSelector(
  selectCashFlowFilterPeriod,
  selectCashFlowFilterYear,
  selectCashFlowFilterMonth,
  selectCashFlowFilterQuarter,
  (period, year, month, quarter) =>
    period === Periods.MONTH ? moment.utc({ year, month }).format('MMM YYYY') : `Quarter ${quarter} ${year}`

);

export const selectCashFlowYears = createSelector(
  selectCashFlowFilterPeriod,
  selectCashFlowDatesMin,
  selectCashFlowDatesMax,
  (period, min?: moment.Moment, max?: moment.Moment) => getYearsDropdownValuesCashFlow(period, min, max)

);

export const selectCashFlowMonths = createSelector(
  selectCashFlowFilterYear,
  selectCashFlowDatesMin,
  selectCashFlowDatesMax,
  (year?: number, min?: moment.Moment, max?: moment.Moment) => getMonthsDropdownValues(year, min, max)

);

export const selectCashFlowQuarters = createSelector(
  selectCashFlowFilterYear,
  selectCashFlowDatesMin,
  selectCashFlowDatesMax,
  (year?: number, min?: moment.Moment, max?: moment.Moment) => getQuartersDropdownValues(year, min, max)

);
